/**
 * ponytail: poolt bestaande EMDR-engine-state via window-globals (geen engine-wijzigingen)
 * en emit activity start/end via de shared usage-tracker. Ceiling: 2s poll → activiteiten
 * korter dan 2s worden mogelijk gemist; oplossing = engine event-hooks (niet nodig nu).
 */
import { useEffect } from "react";
import { trackActivityEnd, trackActivityStart } from "@jari-tools/shared";

const POLL_MS = 2000;

/* eslint-disable @typescript-eslint/no-explicit-any */
type AnyWin = Window &
  typeof globalThis & {
    __EMDR_GET_AUDIENCE__?: () => "volwassenen" | "kinderen";
    __EMDR_GET_STATE__?: () => any;
    EMDRWorkload?: { getState?: () => any };
    EMDRWorkloadKinderen?: { getState?: () => any };
    EMDRDanspas?: { getState?: () => any };
    EMDRRacebaan?: { isActive?: () => boolean };
    EMDRKinderenWgMode?: { readFlags?: () => any };
  };

const WORKLOAD_FLAGS = [
  "rekensommen",
  "rekenreeksen",
  "spellen",
  "spellenAchterstevoren",
  "hoofdsteden",
  "rijmen",
  "kleur",
] as const;

function deriveActivities(win: AnyWin): Map<string, { activity: string; audience: string }> {
  const out = new Map<string, { activity: string; audience: string }>();
  const audience = win.__EMDR_GET_AUDIENCE__?.() ?? "volwassenen";
  const fullState = win.__EMDR_GET_STATE__?.();

  if (audience === "volwassenen") {
    const wl = win.EMDRWorkload?.getState?.() ?? fullState?.workload;
    for (const flag of WORKLOAD_FLAGS) {
      if (wl?.[flag]) {
        const activity = flag === "spellenAchterstevoren" ? "spellen-achterstevoren" : flag;
        out.set(`wl-${flag}`, { activity, audience });
      }
    }
    const danspasOn = win.EMDRDanspas?.getState?.()?.enabled ?? fullState?.danspas?.enabled;
    if (danspasOn) out.set("danspas", { activity: "danspas", audience });
  } else {
    const wk = win.EMDRWorkloadKinderen?.getState?.() ?? fullState?.workloadKinderen;
    if (wk?.plaatjesTellen) out.set("plaatjes-tellen", { activity: "plaatjes-tellen", audience });
    if (wk?.dierenZoeken) out.set("dieren-zoeken", { activity: "dieren-zoeken", audience });
    if (win.EMDRRacebaan?.isActive?.()) out.set("racebaan", { activity: "racebaan", audience });
    const danspasOn = win.EMDRDanspas?.getState?.()?.enabled ?? fullState?.danspas?.enabled;
    if (danspasOn) out.set("danspas", { activity: "danspas", audience });
    const balOn =
      win.EMDRKinderenWgMode?.readFlags?.()?.bal ?? fullState?.kinderenBal;
    if (balOn) out.set("bal", { activity: "bal", audience });
    const stimulus = fullState?.stimulus;
    if (stimulus && stimulus !== "ball") {
      out.set(`figuur-${stimulus}`, { activity: `figuur-${stimulus}`, audience });
    }
  }

  return out;
}

export function useEmdrActivityTracking(booted: boolean): void {
  useEffect(() => {
    if (!booted) return;
    let prev = new Map<string, { activity: string; audience: string }>();

    const tick = () => {
      try {
        const win = window as AnyWin;
        const next = deriveActivities(win);
        for (const [key, info] of next) {
          if (!prev.has(key)) {
            trackActivityStart(key, info.activity, info.audience);
          }
        }
        for (const key of prev.keys()) {
          if (!next.has(key)) {
            trackActivityEnd(key);
          }
        }
        prev = next;
      } catch {
        // analytics mag nooit de tool breken
      }
    };

    tick();
    const interval = window.setInterval(tick, POLL_MS);
    return () => {
      window.clearInterval(interval);
      for (const key of prev.keys()) trackActivityEnd(key);
      prev = new Map();
    };
  }, [booted]);
}
